import React from 'react';
import { CheckCircle2, XCircle, Target } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ChaosToOrder from '../../components/ChaosToOrder';
import SEO from '../../components/SEO';
import GradientOverlay from '../../components/GradientOverlay';
import LogomarkPattern from '../../components/LogomarkPattern';

const CRMImplementation = () => {
  const useCases = [
    {
      title: 'Specialist Engineering Firm',
      location: 'Gold Coast',
      description: 'Tracks long sales cycles for major infrastructure projects, managing tenders and stakeholder relationships with precision.'
    },
    {
      title: 'Wholesale Food Distributor',
      location: 'Brisbane',
      description: 'Automates order tracking and client communication, allowing the sales team to focus on building relationships, not data entry.'
    },
    {
      title: 'SaaS Company',
      location: 'Singapore',
      description: 'Optimises CRM to manage subscription renewals, identify upsell opportunities, and reduce customer churn.'
    }
  ];

  const costsOfInaction = [
    'Lost leads slipping through the cracks',
    'Inaccurate sales forecasting leading to poor business decisions',
    'Wasted sales team time on manual data entry',
    'Inconsistent customer experience damaging reputation',
    'Inability to scale operations effectively'
  ];

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="CRM Implementation Gold Coast"
        description="Expert CRM implementation and optimisation services on the Gold Coast. HubSpot, Salesforce, and Zoho specialists helping Australian SMEs transform customer relationships."
        keywords="CRM Implementation Gold Coast, CRM Consultant Gold Coast, HubSpot Gold Coast, Salesforce Gold Coast, Zoho CRM Australia, CRM Setup Brisbane, Customer Relationship Management Queensland"
        canonical="https://resolvdigital.com.au/services/crm-implementation"
      />
      
      {/* Hero Section - DARK with Background Image */}
      <section style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#0a4447',
        backgroundImage: 'url(https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        padding: '120px 40px 80px'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(10, 68, 71, 0.9)'
        }} />
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ maxWidth: '900px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: 'rgba(13, 115, 119, 0.2)',
              border: '1px solid #0D7377',
              borderRadius: '10rem',
              padding: '8px 20px',
              marginBottom: '24px'
            }}>
              <Target size={20} style={{ color: '#0D7377' }} />
              <span style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#0D7377',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Service
              </span>
            </div>
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(3rem, 6vw, 5rem)',
              lineHeight: 1.1,
              color: '#C9A227',
              textTransform: 'uppercase',
              marginBottom: '32px'
            }}>
              Resolve Your Sales Process.<br />Build a CRM That Works.
            </h1>
            <p style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.5rem',
              lineHeight: 1.6,
              color: '#ffffff'
            }}>
              Transform your sales process with a CRM system that your team will actually use and that delivers a single source of truth for customer data.
            </p>
          </div>
        </div>
      </section>

      {/* What is CRM Implementation - LIGHT TEAL with Chaos Animation */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Chaos to Order Animation as Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.3} />
        </div>
        
        {/* Subtle gradient overlay to blend animation */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 244, 245, 0.4) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(232, 244, 245, 0.3) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                marginBottom: '32px'
              }}>
                What is CRM Implementation & Optimisation?
              </h2>
              <div style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: '#0a4447'
              }}>
                <p style={{ marginBottom: '20px' }}>
                  It's not just about installing software. It's about designing a system that maps to your unique sales process, ensuring adoption and providing a single source of truth for customer data.
                </p>
                <p style={{ marginBottom: '20px' }}>
                  We work with you to understand how your business actually sells, then configure and customise your CRM to match that reality—not force you into a generic template.
                </p>
                <p style={{ fontWeight: 600, color: '#C9A227' }}>
                  The result? A CRM your team will embrace, not resist.
                </p>
              </div>
            </div>
            <div style={{
              backgroundColor: '#ffffff',
              border: '2px solid #0D7377',
              borderRadius: '8px',
              padding: '48px',
              boxShadow: '0 8px 32px rgba(13, 115, 119, 0.1)'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#0a4447',
                marginBottom: '24px',
                textTransform: 'uppercase'
              }}>
                Key Deliverables
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {[
                  'Custom sales pipeline design',
                  'Data migration & cleanup',
                  'Team training & adoption support',
                  'Integration with existing tech',
                  'Reporting & analytics setup'
                ].map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '1rem',
                    color: '#0a4447'
                  }}>
                    <CheckCircle2 size={24} style={{
                      color: '#0D7377',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why It's Relevant - WHITE with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Logomark Pattern */}
        <LogomarkPattern opacity={0.025} density="medium" tint="teal" />
        <GradientOverlay variant="subtle" opacity={0.1} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '32px'
          }}>
            Why It's Critical for SMEs & Scale-Ups
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.7,
            color: '#0a4447',
            maxWidth: '900px',
            margin: '0 auto 48px'
          }}>
            SMEs can't afford the waste of a failed CRM project. A well-implemented CRM levels the playing field, allowing you to compete with larger players through efficiency, data-driven insights, and superior customer relationship management.
          </p>
        </div>
      </section>

      {/* Cost of Inaction - LIGHT TEAL */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="gold" />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            The Hidden Cost of Inaction
          </h2>
          <div style={{
            backgroundColor: '#ffffff',
            border: '2px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            padding: '48px',
            maxWidth: '900px',
            margin: '0 auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {costsOfInaction.map((cost, index) => (
                <li key={index} style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  color: '#1a1c1b'
                }}>
                  <XCircle size={24} style={{
                    color: '#dc3545',
                    flexShrink: 0,
                    marginTop: '4px'
                  }} />
                  <span>{cost}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Use Cases - DARK (contrast section) */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#084144',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.1} />
        </div>
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            color: '#C9A227',
            textTransform: 'uppercase',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Real Australian B2B Success Stories
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#b8d8da',
            maxWidth: '800px',
            margin: '0 auto 80px',
            textAlign: 'center'
          }}>
            See how businesses like yours are thriving with properly implemented CRM systems.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
            gap: '40px'
          }}>
            {useCases.map((useCase, index) => (
              <div key={index} style={{
                backgroundColor: 'rgba(10, 93, 96, 0.6)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(201, 162, 39, 0.3)',
                borderRadius: '8px',
                padding: '40px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#C9A227';
                e.currentTarget.style.transform = 'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(201, 162, 39, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  display: 'inline-block',
                  backgroundColor: '#0D7377',
                  color: '#ffffff',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {useCase.location}
                </div>
                <h3 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#C9A227',
                  marginBottom: '16px'
                }}>
                  {useCase.title}
                </h3>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.7,
                  color: '#ffffff'
                }}>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default CRMImplementation;
